import { formatDistanceToNow, differenceInDays, format } from "date-fns";
import { useRealtimeDeviceDetails } from "@/hooks/use-realtime-device-details";
import { Button } from "@/components/ui/button";
import { Pencil, WifiOff, BatteryFull, BatteryMedium, BatteryLow, Battery, Info, Clock, Camera, CameraOff, MapPin, X, Loader2 } from "lucide-react";
import { AIAnalysisDialog } from "./AIAnalysisDialog";
import { areImagesStopped } from "@/hooks/use-device-image-capture";
import { useState } from "react";
import { ImageQuotaPieChart } from "./ImageQuotaPieChart";
import { utcToLocal } from "@/utils/timezoneUtils";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeviceListItemProps {
  deviceId: string;
  organizationId?: string | null;
  onClick: () => void;
}

const getBatteryIcon = (soc: number | undefined) => {
  if (soc === undefined) return <Battery className="h-4 w-4 text-muted-foreground" />;
  if (soc >= 60) return <BatteryFull className="h-4 w-4 text-green-600" />;
  if (soc >= 25) return <BatteryMedium className="h-4 w-4 text-yellow-600" />;
  return <BatteryLow className="h-4 w-4 text-red-600" />;
};

const getBatteryColor = (soc: number | undefined) => {
  if (soc === undefined) return "text-muted-foreground";
  if (soc >= 60) return "text-green-600";
  if (soc >= 25) return "text-yellow-600";
  return "text-red-600";
};

const formatOperatingHours = (scheduleStart?: string | number, scheduleEnd?: string | number, timezone?: string): string => {
  if (scheduleStart === undefined || scheduleEnd === undefined) return 'Always Active';
  const startHour = typeof scheduleStart === 'string' ? parseInt(scheduleStart) : scheduleStart;
  const endHour = typeof scheduleEnd === 'string' ? parseInt(scheduleEnd) : scheduleEnd;
  if (isNaN(startHour) || isNaN(endHour)) return 'Always Active';
  try {
    const localStartHour = utcToLocal(startHour, timezone || 'UTC');
    const localEndHour = utcToLocal(endHour, timezone || 'UTC');
    const formatHour = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;
    return `${formatHour(localStartHour)}-${formatHour(localEndHour)}`;
  } catch {
    return `${startHour}:00-${endHour}:00`;
  }
};

const formatCaptureInterval = (intervalMs?: string | number): string => {
  if (!intervalMs) return '';
  const ms = typeof intervalMs === 'string' ? parseInt(intervalMs) : intervalMs;
  if (isNaN(ms)) return '';
  const hours = ms / (1000 * 60 * 60);
  if (hours >= 1) return hours === 1 ? '1 hr' : `${hours} hrs`;
  const minutes = ms / (1000 * 60);
  if (minutes >= 1) return `${Math.round(minutes)} min`;
  const seconds = ms / 1000;
  return `${Math.round(seconds)} sec`;
};

export const DeviceListItem = ({ deviceId, organizationId, onClick }: DeviceListItemProps) => {
  const { device, isLoading } = useRealtimeDeviceDetails(deviceId);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { role, isSystemAdmin } = useSimpleRole();
  const canCancelShutdown = role === 'admin' || isSystemAdmin;

  if (isLoading || !device) {
    return (
      <div className="flex items-center gap-4 p-3 rounded-lg border border-input bg-component-background animate-pulse">
        <div className="w-12 h-12 bg-muted rounded-md flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-3 bg-muted rounded w-1/4" />
        </div>
      </div>
    );
  }

  const hasNotification = device.notification?.result?.toLowerCase() === "true";
  const lastUpdatedDate = device.deviceLastUpdated ? new Date(device.deviceLastUpdated) : null;
  const lastUpdatedText = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
    ? formatDistanceToNow(lastUpdatedDate, { addSuffix: true })
    : 'Unknown';

  const deviceStatusRaw = (device as any).deviceStatus ?? (device as any).device_status;
  const isDemo = typeof deviceStatusRaw === 'string' && deviceStatusRaw.toLowerCase() === 'demo';
  const isOffline = isDemo ? false : (lastUpdatedDate ? differenceInDays(new Date(), lastUpdatedDate) >= 2 : false);

  const batterySoC = device.battery?.batterySoC;

  const cameraConfig = device.camera_config as any;
  const deviceTimezone = (device as any).timezone || 'UTC';
  const operatingHours = formatOperatingHours(cameraConfig?.scheduleStart, cameraConfig?.scheduleEnd, deviceTimezone);
  const isEventSchedule = Number(cameraConfig?.enableEventSchedule) === 1;
  const intervalRaw = formatCaptureInterval(
    isEventSchedule ? cameraConfig?.eventScheduleSleepTime : cameraConfig?.withinScheduleSleepTime
  );
  const captureInterval = intervalRaw ? (isEventSchedule ? `Event ${intervalRaw}` : intervalRaw) : '';

  const forceHibernateRaw = cameraConfig?.forceHibernate;
  const isPendingShutdown =
    (forceHibernateRaw === 1 || forceHibernateRaw === "1") &&
    deviceStatusRaw !== 'Hibernate';

  const handleCancelShutdown = async () => {
    setShowCancelDialog(false);
    setIsCancelling(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "cameraConfig.forceHibernate");
      params.append("value", "0");
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!response.ok) throw new Error("Failed");
      toast.success("Pending power off cancelled");
    } catch (e) {
      toast.error("Failed to cancel pending power off");
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusLabel = () => {
    if (isDemo) return 'Demo';
    if (isOffline && deviceStatusRaw !== 'Hibernate') return 'Offline';
    if (deviceStatusRaw === 'RecordOnDemand' || deviceStatusRaw === 'RetryOnDemand') return 'On Demand';
    if (deviceStatusRaw === 'Record') return 'Active';
    return deviceStatusRaw || 'Unknown';
  };

  const getStatusClasses = () => {
    const label = getStatusLabel();
    if (label === 'Offline') return 'bg-red-100 text-red-700';
    if (label === 'Active' || label === 'Retry') return 'bg-green-100 text-green-700';
    if (label === 'On Demand') return 'bg-blue-100 text-blue-700';
    if (label === 'Pause') return 'bg-orange-100 text-orange-700';
    if (label === 'Demo') return 'bg-yellow-100 text-yellow-700';
    if (label === 'Hibernate') return 'bg-gray-800 text-white';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <>
      <div
        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
          hasNotification
            ? "brand-alert-row bg-red-50 border-red-200 hover:bg-red-100"
            : "brand-device-card bg-component-background border-input hover:bg-accent"
        }`}
        onClick={onClick}
      >
        {/* Thumbnail */}
        <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 relative bg-muted">
          {device.imageKit?.thumbnailUrl ? (
            <img
              src={device.imageKit.thumbnailUrl}
              alt={device.deviceName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isOffline ? (
                <WifiOff className="h-5 w-5 text-muted-foreground" />
              ) : (
                <span className="text-[10px] text-muted-foreground">No img</span>
              )}
            </div>
          )}
          {isOffline && device.imageKit?.thumbnailUrl && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <WifiOff className="h-4 w-4 text-white" />
            </div>
          )}
        </div>

        {/* Name + Group */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate text-foreground">
            {device.deviceName || "Unnamed Device"}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {(!device.groupName || device.groupName.toLowerCase() === 'none') ? "No Group" : device.groupName}
          </p>
          {areImagesStopped((device as any).deviceOperationalDays) && (
            <span className="mt-1 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
              <CameraOff className="h-3 w-3" />
              Images stopped
            </span>
          )}
        </div>

        {/* Schedule */}
        <div className="hidden lg:flex flex-col flex-shrink-0 w-28">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{operatingHours}</p>
          </div>
          {captureInterval && (
            <div className="flex items-center gap-1">
              <Camera className="h-3 w-3 text-muted-foreground" />
              <p className="text-[10px] text-muted-foreground">{captureInterval}</p>
            </div>
          )}
        </div>

        {/* Battery */}
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 w-20">
          {getBatteryIcon(batterySoC)}
          <span className={`text-xs font-medium ${getBatteryColor(batterySoC)}`}>
            {batterySoC !== undefined ? `${batterySoC}%` : '—'}
          </span>
        </div>

        {/* GPS */}
        <div className="hidden sm:flex items-center justify-center flex-shrink-0 w-10">
          <MapPin
            className={`h-4 w-4 ${
              (device as any).location_enabled === true || (device as any).location_enabled === 1
                ? 'text-green-600'
                : 'text-red-600'
            }`}
          />
        </div>

        {/* Image Quota */}
        <div className="hidden sm:flex items-center flex-shrink-0">
          <ImageQuotaPieChart
            imagesAllowed={device.contract?.imagesAllowed ?? null}
            imagesCurrent={device.contract?.imagesCurrent ?? null}
          />
        </div>

        {/* Status */}
        <div className="flex-shrink-0">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusClasses()}`}>
            {getStatusLabel()}
          </span>
        </div>

        {/* Pending Shutdown badge */}
        {isPendingShutdown && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-500 text-white">
              <Loader2 className="h-3 w-3 animate-spin" />
              PENDING
              {canCancelShutdown && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCancelDialog(true); }}
                  disabled={isCancelling}
                  className="ml-0.5 p-0.5 rounded-full bg-white/20 hover:bg-white/40 transition-colors disabled:opacity-50"
                  aria-label="Cancel pending power off"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          </div>
        )}

        {/* Last Updated - hidden on small screens */}
        <div className="hidden md:block flex-shrink-0 w-32 text-right">
          <p className="text-xs text-muted-foreground">{lastUpdatedText}</p>
          {lastUpdatedDate && !isNaN(lastUpdatedDate.getTime()) && (
            <p className="text-[10px] text-muted-foreground">
              {format(lastUpdatedDate, 'dd MMM, HH:mm')}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="View AI analysis"
            onClick={(e) => {
              e.stopPropagation();
              setShowAIDialog(true);
            }}
          >
            <Info className={`h-4 w-4 ${hasNotification ? 'text-red-600' : 'text-blue-600'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Edit device"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <Pencil className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      <AIAnalysisDialog device={device} open={showAIDialog} onOpenChange={setShowAIDialog} />
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel pending power off?</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel the queued shutdown for {device.deviceName || "this device"}? The camera will remain active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Keep pending</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.stopPropagation(); handleCancelShutdown(); }}
              className="bg-lime-button text-black hover:bg-lime-button/90"
            >
              Cancel power off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
